import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Row, Col, Flex, Dropdown, Button, Drawer, Typography } from "antd";
import {
  CloseOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import HeaderLogo from "../../assets/images/HeaderLogo.png";
import { useParams } from "react-router-dom";

import Sidebar from "../Sidebar";
import { useBridgeStore } from "../../store/apis/useBridgeStore";
import {
  useChatUserStore,
  useUserSelfStore,
} from "../../store/apis/useUserStore";
import { useChannelStore } from "../../store/apis/useChannelStore";
import ProfileMenu from "./ProfileMenu";
import UserStatusHandler from "./UserStatusHandler";

const Header = () => {
  const { channelId } = useParams();
  const searchInputRef = useRef();

  // Store selectors
  const channelData = useChannelStore((state) => state.channelData);
  const userSelfData = useUserSelfStore((state) => state.userSelfData);
  const chatUserData = useChatUserStore((state) => state.chatUserData);
  const setSearchParticularRecord = useBridgeStore(
    (state) => state.setSearchParticularRecord
  );
  const setSearchMsgText = useBridgeStore((state) => state.setSearchMsgText);
  const setGlobalSectionConfig = useBridgeStore(
    (store) => store.setGlobalSectionConfig
  );

  // Local state
  const [toggleSearchMsg, setToggleSearchMsg] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [theme, setTheme] = useState("light");
  const [openSidebarMenu, setOpenSidebarMenu] = useState(false);

  // Memoized values
  const isNarrowScreen = useMemo(() => window.innerWidth <= 500, []);
  const shouldShowLogo = useMemo(
    () => (!toggleSearchMsg && isNarrowScreen) || !isNarrowScreen,
    [toggleSearchMsg, isNarrowScreen]
  );

  // Theme management
  const applyTheme = useCallback((newTheme) => {
    document.documentElement.setAttribute("data-theme", newTheme);
    localStorage.setItem("theme", newTheme);

    const checkbox = document.getElementById("toggleBtn");
    if (checkbox) {
      checkbox.className = `checkbox ${newTheme}-mode`;
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    applyTheme(newTheme);
  }, [theme, applyTheme]);

  // Search functionality
  const handleSearch = useCallback(() => {
    setSearchMsgText({ value: searchText, timestamp: new Date() });
    setGlobalSectionConfig({
      openedSection: "MessageSearchSection",
      isExpanded: false,
    });
  }, [searchText, setSearchMsgText, setGlobalSectionConfig]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const convertToUnderscore = useCallback((input) => {
    return input.replace(/\s+/g, "_");
  }, []);

  const individualSearchLogic = useCallback(() => {
    const oppositeUserChannelRawData = channelId ? channelData : chatUserData;
    if (!oppositeUserChannelRawData) return;

    const name =
      oppositeUserChannelRawData.username || oppositeUserChannelRawData.name;
    setSearchText(`in:@${convertToUnderscore(name)} `);
    setSearchParticularRecord({
      name,
      id: oppositeUserChannelRawData._id,
      isChannel: !oppositeUserChannelRawData.username,
    });

    searchInputRef.current?.focus();
  }, [
    channelId,
    channelData,
    chatUserData,
    convertToUnderscore,
    setSearchParticularRecord,
  ]);

  const handleCtrlF = useCallback(
    (event) => {
      if (event.ctrlKey && event.key === "f") {
        event.preventDefault();
        individualSearchLogic();
      }
    },
    [individualSearchLogic]
  );

  // Sidebar controls
  const showSidebarMenu = useCallback(() => {
    setOpenSidebarMenu(true);
  }, []);

  const onClose = useCallback(() => {
    setOpenSidebarMenu(false);
  }, []);

  const handleCloseSearch = useCallback(() => {
    if (searchText.length === 0) {
      setToggleSearchMsg(false);
    }
  }, [searchText.length]);

  // Effects
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    setTheme(savedTheme);
    applyTheme(savedTheme);
  }, [applyTheme]);

  useEffect(() => {
    window.addEventListener("keydown", handleCtrlF);
    return () => window.removeEventListener("keydown", handleCtrlF);
  }, [handleCtrlF]);

  return (
    <>
      <Row className="header-wrapper">
        <Col flex="auto">
          <Flex align="center" gap="small">
            <Flex align="center" className="change-chat-prev-next">
              <button className="btn-prev" onClick={showSidebarMenu}></button>
              <div className="channel-wrapper">
                <Button
                  type="text"
                  onClick={showSidebarMenu}
                  className="sidebar-toggler"
                >
                  {openSidebarMenu ? (
                    <MenuFoldOutlined />
                  ) : (
                    <MenuUnfoldOutlined />
                  )}
                </Button>
                <Drawer
                  title={
                    <Flex vertical>
                      <Typography.Title level={5} style={{ marginBottom: 0 }}>
                        {userSelfData?.fullName || userSelfData?.username}
                      </Typography.Title>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: "12px" }}
                      >
                        {userSelfData?.email}
                      </Typography.Text>
                    </Flex>
                  }
                  placement="left"
                  closable={false}
                  onClose={onClose}
                  open={openSidebarMenu}
                  className="mobile-drawer-sidebar"
                  width="250"
                >
                  <Flex vertical className="sidebar ant-layout-sider">
                    <Sidebar />
                  </Flex>
                </Drawer>
              </div>
            </Flex>
            {shouldShowLogo && (
              <div className="sidebar-logo">
                <img src={HeaderLogo} alt="Header Logo" />
              </div>
            )}
          </Flex>
        </Col>

        <Col flex="auto">
          <Dropdown
            className="ec-dropdown header-search-dropdown"
            dropdownRender={ (menu) => (
              <Flex
                vertical
                gap="small"
                className="ec-dropdown__menu header-search-dropdown__menu"
              >
              </Flex>
            ) }
            trigger={["click"]}
          >
            <div
              className={`search-container ${
                toggleSearchMsg ? "msgSearchOpen" : ""
              }`}
              onChange={(e) => setSearchText(e.target.value)}
            >
              <Button
                onClick={() => setToggleSearchMsg(true)}
                className="search-icon"
              >
                <i className="fa-solid fa-magnifying-glass"></i>
              </Button>
              <input
                ref={searchInputRef}
                type="text"
                className="custom-search"
                placeholder="Search"
                value={searchText}
                onKeyDown={handleKeyPress}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText.length > 0 && (
                <div className="clear-search" onClick={() => setSearchText("")}>
                  <CloseOutlined />
                </div>
              )}
              <Button className="closeBtn" onClick={handleCloseSearch}>
                <i className="fa-solid fa-circle-chevron-right"></i>
              </Button>
            </div>
          </Dropdown>
        </Col>

        <Col flex="auto">
          <Flex
            justify="end"
            align="center"
            gap="small"
            className="menu-controls"
          >
            <div className="toggle-button">
              <input
                type="checkbox"
                className="checkbox"
                id="toggleBtn"
                checked={theme === "dark"}
                onChange={toggleTheme}
              />
              <label className="switch" htmlFor="toggleBtn">
                <i className="fas fa-moon"></i>
                <i className="fas fa-sun"></i>
              </label>
              <div className="ball"></div>
            </div>

            <ProfileMenu />
          </Flex>
        </Col>
      </Row>

      {/* <UserStatusHandler userData={userSelfData} /> */}
    </>
  );
};

export default Header;
